import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { createFileRoute } from "@tanstack/react-router";
import { Check } from "lucide-react";

import { GoalStatus } from "~/../drizzle/schema";
import { ErrorStateCard } from "~/components/error-state-card";
import { Avatar, AvatarImage, AvatarFallback } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import {
  GoalStatusBadge,
  GoalCategoryBadge,
  GoalTimelineBadge,
} from "~/components/ui/goal-badges";
import { RichTextViewer } from "~/components/ui/rich-text-editor";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/cn";
import { getGoalByShareSlugFn } from "~/lib/functions/goal-of-life";
import { MilestoneStatusBadge } from "~/routes/_user/life-design/-components/milestone-status-badge";

export const Route = createFileRoute("/_public/goals/$shareSlug")({
  component: SharedGoalPage,
});

function DescriptionViewer({ description }: { description: string }) {
  return <RichTextViewer content={description} />;
}

function SharedGoalPage() {
  const { shareSlug } = Route.useParams();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["shared-goal", shareSlug],
    queryFn: () => getGoalByShareSlugFn({ data: { shareSlug } }),
    enabled: !!shareSlug,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-12">
        <div className="mx-auto max-w-2xl">
          <Card className="p-8">
            <div className="space-y-4">
              <Skeleton className="h-12 w-12 rounded-xl" />
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-24 w-full" />
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-12">
        <div className="mx-auto max-w-2xl">
          <ErrorStateCard
            title="Goal not found"
            message="This goal may have been removed or the link is invalid."
            action={
              <Link to="/">
                <Button>Go to Home</Button>
              </Link>
            }
          />
        </div>
      </div>
    );
  }

  const { goal, category, user, milestones } = data;
  const completedMilestones = milestones.filter(
    (m) => m.status === GoalStatus.COMPLETED,
  ).length;
  const totalMilestones = milestones.length;

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-12">
      <div className="mx-auto max-w-4xl space-y-4">
        <Card>
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12 rounded-xl">
              <AvatarImage
                src={user.profilePictureUrl || undefined}
                alt={`${user.firstName} ${user.lastName}`}
              />
              <AvatarFallback className="rounded-xl bg-purple-100 text-lg font-medium text-purple-600">
                {user.firstName?.[0]}
                {user.lastName?.[0]}
              </AvatarFallback>
            </Avatar>
            <span className="text-lg font-medium text-gray-900">
              {user.firstName} {user.lastName}
            </span>
          </div>

          <h1 className="text-2xl font-medium text-gray-900">{goal.title}</h1>

          <div className="flex flex-wrap items-center gap-2">
            <GoalStatusBadge status={goal.status} />
            <GoalCategoryBadge category={category.name} />
            <GoalTimelineBadge timeline={goal.timeline} />
          </div>

          {goal.description && (
            <DescriptionViewer description={goal.description} />
          )}

          {milestones.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  Milestones
                </h2>
                <span className="text-sm text-gray-500">
                  Completed {completedMilestones} of {totalMilestones}{" "}
                  milestones
                </span>
              </div>

              <div className="h-2 w-full rounded-full bg-gray-200">
                <div
                  className="h-2 rounded-full bg-purple-600 transition-[width]"
                  style={{
                    width: `${totalMilestones > 0 ? (completedMilestones / totalMilestones) * 100 : 0}%`,
                  }}
                />
              </div>

              <div className="space-y-3">
                {milestones.map((milestone) => (
                  <div
                    key={milestone.id}
                    className="flex items-center justify-between rounded-xl bg-gray-50 p-4"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={cn(
                          "flex h-5 w-5 items-center justify-center rounded border-2",
                          milestone.status === GoalStatus.COMPLETED
                            ? "border-purple-600 bg-purple-600"
                            : "border-gray-300",
                        )}
                      >
                        {milestone.status === GoalStatus.COMPLETED && (
                          <Check className="h-3 w-3 text-white" />
                        )}
                      </div>
                      <span
                        className={cn(
                          "text-base",
                          milestone.status === GoalStatus.COMPLETED
                            ? "text-gray-500"
                            : "text-gray-900",
                        )}
                      >
                        {milestone.title}
                      </span>
                    </div>
                    <MilestoneStatusBadge status={milestone.status} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        <Card className="dcm-shiny bg-purple-700 p-8 text-center text-white">
          <h2 className="mb-2 text-2xl font-semibold">
            Track your own goals with
          </h2>
          <h3 className="mb-6 text-2xl font-semibold">Dream Coach Match</h3>
          <Link to="/login">
            <Button className="bg-white px-8 text-purple-700 hover:bg-gray-100">
              Create a Free Account
            </Button>
          </Link>
        </Card>
      </div>
    </div>
  );
}
